import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { reviewApi, type ReviewItem } from "../../../api/clock";
import { venueApi } from "../../../api/resources";
import type { VenueAdmin } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { StatusPill } from "../../../components/ui";
import { DataTable, LoadState, useLoad } from "../shared";
import { ReviewDrawer } from "./ReviewDrawer";

const CODES = ["NO_ASSIGNMENT", "NO_APPROVED_ASSIGNMENT", "DUPLICATE_IN", "OPEN_SESSION_NEEDS_REVIEW", "OUT_WHILE_IN_PENDING",
  "OUT_WITHOUT_OPEN_SESSION", "LINK_EXPIRED", "STALE", "INACCURATE", "OUT_OF_RANGE", "GPS_DENIED", "GPS_UNSUPPORTED", "GPS_UNAVAILABLE",
  "GPS_TIMEOUT", "ASSISTANCE_REQUESTED", "PHONE_NOT_VERIFIED", "SMS_OPTED_OUT", "MISSING_OUT", "OUT_NOT_VERIFIED",
  "CORRECTION_MISSED_IN", "CORRECTION_MISSED_OUT", "CORRECTION_WRONG_TIME", "CORRECTION_OTHER"];
const KIND_PATH = { EXCEPTION: "exceptions", SESSION: "sessions", CORRECTION: "corrections" } as const;
const sel = "h-10 rounded-sm border border-white/15 bg-surface px-3 text-xs text-white";

export default function AdminReview() {
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({ status: "OPEN", code: params.get("code") ?? "", venue_id: "", date: "", employee_id: params.get("employee_id") ?? "" });
  const [reload, setReload] = useState(0);
  const [venues, setVenues] = useState<VenueAdmin[]>([]);
  const open = params.get("open");
  useEffect(() => { venueApi.list().then(setVenues).catch(() => setVenues([])); }, []);
  const loader = useCallback(() => reviewApi.list({ ...f }), [f, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<ReviewItem[]>(loader);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const [okind, oid] = open?.split(":") ?? [];
  return (
    <section data-testid="admin-review-page" className="animate-rise">
      <PageHeader title="Exceptions & Corrections" />
      <div className="mb-4 flex flex-wrap gap-2">
        <select value={f.status} onChange={set("status")} className={sel} data-testid="review-filter-status"><option value="">All</option><option value="OPEN">Open</option><option value="RESOLVED">Resolved</option></select>
        <select value={f.code} onChange={set("code")} className={sel} data-testid="review-filter-code"><option value="">All codes</option>{CODES.map((c) => <option key={c}>{c}</option>)}</select>
        <select value={f.venue_id} onChange={set("venue_id")} className={sel} data-testid="review-filter-venue"><option value="">All venues</option>{venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <input type="date" value={f.date} onChange={set("date")} className={sel} data-testid="review-filter-date" />
        {f.employee_id && <button type="button" onClick={() => setF((x) => ({ ...x, employee_id: "" }))} className="text-xs text-signal" data-testid="review-clear-employee">Clear employee filter ×</button>}
      </div>
      <LoadState error={error} loading={!data && !error} testId="review-load-state" />
      {data && (
        <DataTable testId="review-table" headers={["Received", "Employee", "Venue", "Kind", "Code", "Status", "Note"]} empty={data.length === 0}>
          {data.map((i) => (
            <tr key={`${i.kind}-${i.id}`} data-testid={`review-row-${i.id}`} onClick={() => setParams({ open: `${KIND_PATH[i.kind]}:${i.id}` })} className="cursor-pointer hover:bg-white/[0.04]">
              <td className="px-4 py-3 text-xs">{i.received_local}</td>
              <td className="px-4 py-3 text-xs text-white">{i.employee_name}</td>
              <td className="px-4 py-3 text-xs">{i.venue_name ?? "—"}</td>
              <td className="px-4 py-3 text-[11px] uppercase text-zinc-400">{i.kind} {i.type}</td>
              <td className="px-4 py-3 font-mono text-xs text-warn">{i.code}</td>
              <td className="px-4 py-3"><StatusPill status={i.status} /></td>
              <td className="max-w-xs truncate px-4 py-3 text-xs">{i.note ?? ""}</td>
            </tr>
          ))}
        </DataTable>
      )}
      {okind && oid && <ReviewDrawer kind={okind} id={oid} onClose={() => setParams({})} onChanged={() => setReload((r) => r + 1)} />}
    </section>
  );
}
